<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('add-vital-entry-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text
			v-if="selectedCCMRecord === null"
			class="pa-4 overflow-y-auto text-center d-flex flex-column justify-center overflow-x-hidden"
			style="height: calc(100vh - 62px)">
			<v-icon color="grey" size="50" class="mb-3">mdi-alert-circle-outline</v-icon>
			<p class="font-responsive-lg lightgrey">
				{{ $t('select-a-record') }}
			</p>
		</v-card-text>

		<v-card-text v-else class="pa-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						:label="$t('type')"
						v-model="selectedType"
						:items="vitalEntryTypeTemplate"
						item-text="value"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }"
						@change="getUnits">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">
										{{ item.value }}
									</span>
								</template>
								{{ item.description }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
			</v-row>

			<v-row dense class="ma-0">
				<v-col cols="6">
					<v-text-field :label="$t('value')" v-model="vitalValue" outlined hide-details />
				</v-col>
				<v-col cols="6">
					<v-select
						:label="$t('unit')"
						v-model="selectedUnit"
						:disabled="!selectedType"
						:items="availableUnits"
						item-text="unit"
						outlined
						hide-details
						:menu-props="{ maxHeight: 150, offsetY: true }">
						<template v-slot:item="{ item }">
							{{ item.unit }}
						</template>
					</v-select>
				</v-col>
			</v-row>

			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-textarea v-model="notes" :label="$t('note')" outlined no-resize hide-details rows="12" />
				</v-col>
			</v-row>
		</v-card-text>

		<v-container v-if="selectedCCMRecord" class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-tooltip top v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addVitalEntry()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';
export default {
	name: 'CCMVitalEntryAdd',
	data () {
		return {
			notes: this.$t('manual-reading'),
			vitalValue: '',
			selectedUnit: '',
			availableUnits: [],
			selectedType: '',
			timeSpentSeconds: 0,
			vitalEntryTypeTemplate: null,
			vitalEntryUnitTemplate: null,
		};
	},
	computed: {
		...mapState({
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),
		addButtonEnabled () {
			return (
				this.notes.trim() &&
				this.selectedUnit &&
				this.selectedType &&
				this.timeSpentSeconds &&
				this.timeSpentSeconds <= 7200
			);
		},
	},
	methods: {
		...mapActions({
			addCCMVitalEntry: 'ccmVitalEntry/addCCMVitalEntry',
			getAllVitalTypeOptions: 'ccmRecords/getVitalTypeOptionTemplate',
			getAllVitalUnitOptions: 'ccmRecords/getVitalUnitOptionTemplate',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),

		getUnits () {
			if (this.selectedType) {
				this.availableUnits = this.vitalEntryUnitTemplate.filter((unit) => unit.vitalType === this.selectedType);
				this.selectedUnit = this.availableUnits[0].unit;
			}
		},

		async addVitalEntry () {
			const request = {
				ccmRecordId: this.selectedCCMRecord.id,
				patientId: this.selectedCCMRecord.patientId,
				hospitalId: this.selectedCCMRecord.hospitalId,
				vitalDate: new Date().toISOString(),
				vitalType: this.selectedType,
				value: this.vitalValue,
				unit: this.selectedUnit,
				notes: this.notes,
				performedBy: `${this.userData.firstName} ${this.userData.lastName}`,
				performedByRole: this.userData.roleData.roleName,
				staffId: this.userData.id,
				timeSpentSeconds: parseInt(this.timeSpentSeconds),
				isRPMBillable: false,
				additionalData: '',
			};
			await this.addCCMVitalEntry(request);
			await this.storeCCMRecordsByPatientId(this.selectedCCMRecord.patientId);
			this.closeDrawer();
		},

		closeDrawer () {
			this.selectedUnit = null;
			this.selectedType = null;
			this.vitalValue = null;
			this.timeSpentSeconds = 0;
			this.notes = this.$t('manual-reading');
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
	},

	async mounted () {
		if (!this.vitalEntryUnitTemplate) {
			const response = await this.getAllVitalUnitOptions();
			this.vitalEntryUnitTemplate = response.data;
		}
		if (!this.vitalEntryTypeTemplate) {
			const response = await this.getAllVitalTypeOptions();
			this.vitalEntryTypeTemplate = response.data;
		}
	},
	components: { InputTimeFormatter },
};
</script>
